/**
 * Tier card — centerpiece. Tier name + tagline + total warmth.
 */
import { store } from '../store.ts';
import { TIER_DISPLAY, TIER_THRESHOLDS_ACTIVE_COUNT, TIERS } from '../types.ts';
import type { Tier } from '../types.ts';

const TIER_TONE: Record<Tier, { fg: string; accent: string; glow: string }> = {
  loner:          { fg: 'oklch(40% 0.06 25)', accent: 'oklch(85% 0.05 25)', glow: 'oklch(78% 0.06 25 / 0.25)' },
  small_circle:   { fg: 'oklch(36% 0.10 25)', accent: 'oklch(82% 0.08 50)', glow: 'oklch(78% 0.10 55 / 0.28)' },
  active_network: { fg: 'oklch(32% 0.11 22)', accent: 'oklch(80% 0.10 60)', glow: 'oklch(76% 0.12 60 / 0.32)' },
  wide_network:   { fg: 'oklch(30% 0.12 20)', accent: 'oklch(78% 0.12 65)', glow: 'oklch(72% 0.14 65 / 0.36)' },
  connector:      { fg: 'oklch(26% 0.13 18)', accent: 'oklch(75% 0.14 70)', glow: 'oklch(68% 0.16 70 / 0.42)' },
};

const TIER_PROMOTION: Record<Tier, string> = {
  loner:          'Four real ones is a circle. Pick up the phone.',
  small_circle:   'Chaar yaar, locked. Now widen the dial — the city wants in.',
  active_network: 'Active. Every shaam ki mehfil has your seat. One more lap.',
  wide_network:   'Wide. Har city mein dost. The bridge is one introduction away.',
  connector:      'Connector. 47 people who’d take your call at midnight.',
};

export class DialTierCard extends HTMLElement {
  #shadow = this.attachShadow({ mode: 'open' });
  #unsub?: () => void;

  connectedCallback(): void {
    this.#unsub = store.subscribe((s) => {
      if (s.summary) this.#render(s.summary.tier, s.summary.warmth_total, s.summary.active_connection_count);
    });
  }
  disconnectedCallback(): void { this.#unsub?.(); }

  #render(tier: Tier, warmth: number, active: number): void {
    const tone = TIER_TONE[tier];
    const meta = TIER_DISPLAY[tier];
    const idx = TIERS.indexOf(tier);
    const next = idx >= 0 && idx < TIERS.length - 1 ? (TIERS[idx + 1] as Tier) : null;
    const toNext = next != null
      ? Math.max(0, TIER_THRESHOLDS_ACTIVE_COUNT[next] - active)
      : 0;
    const tierFloor = TIER_THRESHOLDS_ACTIVE_COUNT[tier];
    const tierCeil  = next != null ? TIER_THRESHOLDS_ACTIVE_COUNT[next] : tierFloor + 1;
    const progressPct = next != null
      ? Math.max(0, Math.min(100, ((active - tierFloor) / Math.max(1, tierCeil - tierFloor)) * 100))
      : 100;
    const trajectoryLabel = next != null
      ? `${meta.name.toUpperCase()} &rarr; ${TIER_DISPLAY[next].name.toUpperCase()}`
      : `${meta.name.toUpperCase()} &middot; the bridge`;
    const toNextCopy = next != null && toNext > 0
      ? `${toNext} more to ${TIER_DISPLAY[next].name.toLowerCase()}`
      : 'arrived';
    const promo = TIER_PROMOTION[tier];
    this.#shadow.innerHTML = /* html */ `
<style>
:host {
  display: block;
  --tone-fg: ${tone.fg};
  --tone-accent: ${tone.accent};
  --tone-glow: ${tone.glow};
  background: linear-gradient(155deg, var(--tone-accent) 0%, var(--paper-card) 70%);
  border: 1px solid var(--rule);
  border-radius: 10px;
  padding: 30px 26px 32px;
  position: relative;
  overflow: hidden;
  isolation: isolate;
  view-transition-name: tier-card;
}

:host::before {
  content: '';
  position: absolute;
  inset: 1px;
  border-radius: 9px;
  background: var(--paper-card);
  z-index: 0;
  box-shadow: inset 0 1px 0 color-mix(in oklch, var(--tone-accent) 40%, transparent);
}

:host::after {
  content: '';
  position: absolute;
  inset: 0;
  background: radial-gradient(ellipse at 85% -20%, var(--tone-glow), transparent 60%);
  pointer-events: none;
  z-index: 0;
}

.body { position: relative; z-index: 1; }

.label {
  font-family: var(--mono);
  font-size: 10px;
  letter-spacing: 0.45em;
  text-transform: uppercase;
  color: var(--tone-fg);
  opacity: 0.75;
}

.name {
  font-family: var(--display);
  font-size: clamp(36px, 9cqw, 56px);
  font-weight: 600;
  margin-top: 6px;
  letter-spacing: -0.012em;
  color: var(--tone-fg);
  font-style: italic;
  line-height: 1;
  text-wrap: balance;
  font-feature-settings: 'kern' 1, 'cv11' 1, 'ss01' 1;
}

.rule-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 18px 0;
}

.rule {
  width: 38px;
  height: 1px;
  background: var(--tone-fg);
  opacity: 0.4;
}

.rule-dot {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: var(--tone-fg);
  opacity: 0.55;
}

.tagline {
  font-family: var(--display);
  font-style: italic;
  font-size: 18px;
  color: var(--ink-dim);
  font-weight: 400;
  text-wrap: balance;
}

.metrics {
  display: flex;
  gap: 30px;
  margin-top: 24px;
  flex-wrap: wrap;
  padding-top: 18px;
  border-top: 1px dashed var(--rule);
}

.metric-label {
  font-family: var(--mono);
  font-size: 9px;
  color: var(--ink-faint);
  letter-spacing: 0.35em;
  text-transform: uppercase;
}

.metric-num {
  font-family: var(--display);
  font-size: 36px;
  font-weight: 700;
  color: var(--ink);
  letter-spacing: -0.022em;
  line-height: 1;
  font-feature-settings: 'tnum' 1, 'ss01' 1, 'cv11' 1, 'lnum' 1;
  margin-top: 6px;
}

.trajectory {
  position: relative;
  z-index: 1;
  margin-top: 22px;
  padding-top: 18px;
  border-top: 1px solid color-mix(in oklch, var(--tone-fg) 18%, transparent);
  display: grid;
  gap: 10px;
}

.trajectory-head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  flex-wrap: wrap;
}

.trajectory-label {
  font-family: var(--mono);
  font-size: 9.5px;
  letter-spacing: 0.4em;
  text-transform: uppercase;
  color: var(--tone-fg);
  opacity: 0.85;
  font-feature-settings: 'tnum' 1;
}

.trajectory-tail {
  font-family: var(--mono);
  font-size: 9.5px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--ink-faint);
  font-feature-settings: 'tnum' 1;
}

.trajectory-rail {
  height: 4px;
  background: color-mix(in oklch, var(--tone-fg) 10%, transparent);
  border-radius: 999px;
  overflow: hidden;
  position: relative;
}
.trajectory-rail::before {
  content: '';
  position: absolute;
  inset: 0;
  width: var(--progress);
  background: linear-gradient(90deg, var(--tone-fg), color-mix(in oklch, var(--tone-fg) 70%, var(--brass-deep)));
  border-radius: 999px;
  animation: rail-fill 900ms var(--snap) both;
}
@keyframes rail-fill {
  from { transform: scaleX(0); transform-origin: left; }
  to   { transform: scaleX(1); }
}

.trajectory-promo {
  font-family: var(--display);
  font-style: italic;
  font-size: 17px;
  color: var(--ink);
  text-wrap: balance;
  line-height: 1.32;
  margin-top: 4px;
  font-weight: 500;
}

.trajectory-promo::before {
  content: '\\201C';
  font-family: var(--display);
  font-weight: 700;
  font-size: 28px;
  color: var(--tone-fg);
  opacity: 0.7;
  margin-right: 4px;
  vertical-align: -6px;
}
.trajectory-promo::after {
  content: '\\201D';
  font-family: var(--display);
  font-weight: 700;
  font-size: 28px;
  color: var(--tone-fg);
  opacity: 0.7;
  margin-left: 2px;
  vertical-align: -6px;
}

@media (prefers-reduced-motion: reduce) {
  .trajectory-rail::before { animation: none; }
}
</style>
<div class="body">
  <div class="label">aapki mehfil</div>
  <div class="name">${meta.name}</div>
  <div class="rule-wrap">
    <span class="rule"></span>
    <span class="rule-dot"></span>
  </div>
  <div class="tagline">&ldquo;${meta.tagline}&rdquo;</div>
  <div class="metrics">
    <div>
      <div class="metric-label">avg warmth</div>
      <div class="metric-num">${Math.round(warmth)}</div>
    </div>
    <div>
      <div class="metric-label">active conn.</div>
      <div class="metric-num">${active}</div>
    </div>
  </div>
  <div class="trajectory" style="--progress:${progressPct.toFixed(1)}%">
    <div class="trajectory-head">
      <span class="trajectory-label">${trajectoryLabel}</span>
      <span class="trajectory-tail">${toNextCopy}</span>
    </div>
    <div class="trajectory-rail" aria-hidden="true"></div>
    <p class="trajectory-promo">${promo}</p>
  </div>
</div>
`;
  }
}

customElements.define('dial-tier-card', DialTierCard);
